import { useEffect, useRef } from 'react';

import { Button } from '@/components/Button';
import { useTranslation } from '@/i18n/useTranslation';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

export interface ConsentDialogProps {
  /** `true` for *I agree*, `false` for *I do not agree*. The gate reads it; nothing else does. */
  readonly onAnswer: (consented: boolean) => void;
}

/**
 * The consent to contact, as a BLOCKING modal [decision 0160; Figma `04a · Consent to contact (dialog on
 * Continue)` → `Overlay · Consent to contact — Desktop / iPad / Mobile`].
 *
 * **It is a gate, not a checkbox, and it is not on the profile screen.** ISS ruled both on 2026-09-03 —
 * Heidi Carrel: *"I do like having it as a pop-up. I think that's easier… it's a lot of verbiage and having
 * it on that screen and having it small and them scrolling through it seemed to be a lot."* The build
 * carried an inline checkbox until this landed, which is [0156]'s shape, not [0160]'s.
 *
 * **Neither Escape nor a tap outside dismisses it**, and that is the one place this deliberately parts
 * company with `DateCalendar`, the app's other dialog. 0160: *"There is no path past the consent without
 * agreeing, and no silent dead end either."* A dismissable consent is a fourth way out of a screen that has
 * exactly two, and on a kiosk the accidental dismissal is not hypothetical — a candidate resting a palm on a
 * touchscreen closes an overlay without meaning to. Declining is a choice they make with a button, and it
 * lands on `Blocked · consent` rather than nowhere.
 *
 * ⚠️ **The copy is ISS's, from the frame, and the three frames DISAGREE about its first clause.** Desktop
 * opens *"By selecting "Agree"…"*; iPad and Mobile still open *"By checking this box…"*, which is the
 * inline-checkbox wording [0156] and describes a control this modal does not have. Desktop's is used, at all
 * three widths, because the other is false on the screen it is drawn on. [0040] reserves this copy to ISS,
 * so the divergence is reported rather than resolved here — see the task's evidence notes.
 */
export function ConsentDialog({ onAnswer }: ConsentDialogProps) {
  const t = useTranslation();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const dialog = useRef<HTMLDivElement | null>(null);

  /**
   * Tab stays inside the dialog.
   *
   * The scrim already stops a POINTER reaching the form underneath — it covers the viewport — but nothing
   * stops Tab, and a blocking modal a keyboard user can tab out of is not blocking. `aria-modal` tells a
   * screen reader to ignore the background; it does not move focus, and no browser implements it as a trap.
   *
   * Cheaper than `inert` on everything else: that would mean giving the shared kiosk shell a prop it has one
   * caller for, and it is the background that would then own a rule belonging to this dialog.
   */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || dialog.current === null) return;
      const stops = [...dialog.current.querySelectorAll<HTMLElement>('button')];
      const first = stops[0];
      const last = stops[stops.length - 1];
      if (first === undefined || last === undefined) return;
      /*
       * `at` is −1 both for "not a stop" and for the heading, which is where focus lands on mount and which
       * is not tabbable itself. That is the case worth spelling out: Tab from the heading walks forwards into
       * the buttons on its own, but Shift+Tab from it walks straight out of the dialog, so it has to wrap
       * too. Treating `at <= 0` as the backward edge covers both it and the first button.
       */
      const at = stops.indexOf(document.activeElement as HTMLElement);
      const backwards = event.shiftKey && at <= 0;
      const forwards = !event.shiftKey && at === stops.length - 1;
      if (!backwards && !forwards) return;
      // Wrapping is what makes it a loop rather than a wall: at either end the focus jumps to the other.
      event.preventDefault();
      (backwards ? last : first).focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  return (
    /*
      The scrim is a real element rather than a background on the positioning layer, because the frame draws
      it as one — `Scrim (overlay exception)`, #000000 at 45% over the whole viewport — and it is the only
      overlay in the design system allowed to darken the page.

      `fixed`, not absolute: the profile form is taller than the viewport, so an absolutely positioned scrim
      would end where the card ends and leave the bottom of a scrolled form uncovered and tappable.
    */
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--scrim)] p-6">
      {/*
        512 at desktop, 480 at iPad, 327 at mobile — the frame's three widths, read live 2026-09-10
        (12389:15490 / 15504 / 15518). The mobile number is the viewport less 24 a side, which is what the
        `p-6` on the scrim already produces, so it needs no rule of its own.

        `max-h` plus scroll because the body is ISS's full legal text: the mobile modal is drawn 716 tall in
        an 812 frame, so a shorter device — or Spanish, which runs longer — would push the two buttons off
        the bottom of a screen with no way to reach them.
      */}
      <div
        aria-labelledby="consent-dialog-heading"
        aria-modal="true"
        className="flex max-h-full w-full max-w-[480px] flex-col gap-4 overflow-y-auto rounded-[var(--radius-lg)] bg-[var(--card)] p-6 shadow-[shadow:var(--elevation-3)] min-[1440px]:max-w-[512px]"
        ref={dialog}
        role="dialog"
      >
        <h2
          className="kiosk-heading-focus w-full text-base font-semibold leading-6 text-[var(--foreground)]"
          id="consent-dialog-heading"
          ref={heading}
          tabIndex={-1}
        >
          {t('application.consentHeading')}
        </h2>
        <p className="w-full text-sm leading-5 text-[var(--muted-foreground)]">
          {t('consent.review')}
        </p>
        {/*
          12/18, which is the frame's own size for this block and smaller than anything else on the kiosk.
          It is ISS's legal text reproduced verbatim — shortening or summarising it is not ours to do — and
          the modal exists precisely so that it gets the whole screen instead of a scrolling box on a form.
        */}
        <p className="w-full text-xs leading-[18px] text-[var(--muted-foreground)]">
          {t('application.consentFull')}
        </p>
        <p className="w-full text-sm font-semibold leading-5 text-[var(--muted-foreground)]">
          {t('consent.question')}
        </p>
        {/*
          Equal halves at every width: the frame draws 226 + 12 + 226 in a 464 row at desktop, 210 + 12 + 210
          in 432 at iPad and 134 + 12 + 134 in 279 at mobile. It does NOT stack at 375 — the two labels fit —
          so this row stays a row, unlike the I-9's.
        */}
        <div className="flex w-full gap-3">
          <Button
            fullWidth
            onClick={() => {
              onAnswer(true);
            }}
          >
            {t('consent.agree')}
          </Button>
          <Button
            fullWidth
            onClick={() => {
              onAnswer(false);
            }}
            variant="outline"
          >
            {t('consent.decline')}
          </Button>
        </div>
      </div>
    </div>
  );
}
